import { toast } from "react-toastify";
import { DateTimeUtil } from "../../utils";

type ScheduleOverlapWarning = {
    employee_name?: string | null;
    start_datetime?: string | null;
    end_datetime?: string | null;
    message?: string | null;
};

const getPayloadOverlapWarnings = (payload: any): ScheduleOverlapWarning[] => {
    const dataWarnings = payload?.data?.overlap_warnings;
    const topLevelWarnings = payload?.overlap_warnings;
    if (Array.isArray(dataWarnings)) return dataWarnings;
    if (Array.isArray(topLevelWarnings)) return topLevelWarnings;
    return [];
};

const hasPayloadOverlapWarnings = (payload: any): boolean =>
    Boolean(payload?.data?.has_overlap_warnings || payload?.has_overlap_warnings || getPayloadOverlapWarnings(payload).length > 0);

const formatOverlapDateTimeRange = (start?: string | null, end?: string | null): string => {
    if (!start || !end) return "";

    const startDate = DateTimeUtil.formatWithDayjs(start, "MMM D");
    const startTime = DateTimeUtil.formatWithDayjs(start, "h:mmA");
    const endDate = DateTimeUtil.formatWithDayjs(end, "MMM D");
    const endTime = DateTimeUtil.formatWithDayjs(end, "h:mmA");

    if ([startDate, startTime, endDate, endTime].some((value) => value === "Invalid Date")) {
        return "";
    }

    if (DateTimeUtil.formatWithDayjs(start, "YYYY-MM-DD") === DateTimeUtil.formatWithDayjs(end, "YYYY-MM-DD")) {
        return `${startDate}, ${startTime} - ${endTime}`;
    }

    return `${startDate}, ${startTime} - ${endDate}, ${endTime}`;
};

const formatOverlapWarning = (warning: ScheduleOverlapWarning): string => {
    const employeeName = warning.employee_name?.trim() || "Employee";
    const dateTimeRange = formatOverlapDateTimeRange(warning.start_datetime, warning.end_datetime);

    if (dateTimeRange) {
        return `${employeeName}, ${dateTimeRange}`;
    }

    return warning.message || `${employeeName} overlaps existing schedule`;
};

const buildOverlapToastMessage = (payload: any, fallbackMessage: string): string => {
    const warnings = getPayloadOverlapWarnings(payload);

    if (!warnings.length) {
        return payload?.message || fallbackMessage;
    }

    const firstWarning = formatOverlapWarning(warnings[0]);

    if (warnings.length === 1) {
        return `Schedule saved with overlap warning: ${firstWarning}.`;
    }

    return `Schedules saved with ${warnings.length} overlap warnings. First: ${firstWarning}.`;
};

export const showScheduleResultToast = (payload: any, fallbackMessage: string) => {
    if (hasPayloadOverlapWarnings(payload)) {
        toast.warning(buildOverlapToastMessage(payload, fallbackMessage));
        return;
    }

    toast.success(payload?.message || fallbackMessage);
};
